import { TypographyP2Bold } from '@heidi/design-system/typography';
import { type CountryType } from '@heidi/utils/countries';
import { useIntl } from 'react-intl';

import { getOnboardingProfileHost } from '../../../onboarding-profile-host';
import { subdivisionLabelMessages } from './country-subdivision-labels.messages';
import { roleStepMessages } from './role-step.messages';

interface CountryStateProps {
  country: CountryType | null;
  jurisdiction: string;
  showJurisdiction: boolean;
  /**
   * Offer only this region's countries (plus the enterprise variants for an
   * enterprise region); every country when absent.
   */
  regionFilter?: string;
  onCountrySelected: (value: CountryType | null) => void;
  onJurisdictionChange: (value: string) => void;
}

/** Country (+ State, for AU/US) of the onboarding about-you step. */
export function CountryState({
  country,
  jurisdiction,
  showJurisdiction,
  regionFilter,
  onCountrySelected,
  onJurisdictionChange,
}: CountryStateProps) {
  const { CountrySelect, JurisdictionSelect, enterpriseCountries, isEnterpriseRegion } =
    getOnboardingProfileHost();
  const intl = useIntl();
  // The enterprise country variants are not in the default list.
  const offersEnterpriseCountries = isEnterpriseRegion(regionFilter);

  return (
    <>
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <TypographyP2Bold>
          {intl.formatMessage(roleStepMessages.country)}
        </TypographyP2Bold>
        <CountrySelect
          value={country ?? undefined}
          setValue={onCountrySelected}
          regionFilter={regionFilter}
          extraCountries={offersEnterpriseCountries ? enterpriseCountries : undefined}
          className="[&_button]:bg-surface"
          popoverClassName="min-w-64"
        />
      </div>
      {showJurisdiction ? (
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <TypographyP2Bold>
            {intl.formatMessage(subdivisionLabelMessages.state)}
          </TypographyP2Bold>
          <JurisdictionSelect
            countryName={country?.name}
            value={jurisdiction || null}
            onChange={onJurisdictionChange}
            className="bg-surface"
          />
        </div>
      ) : null}
    </>
  );
}
